import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>;

/**
 * Every password field in the app: hidden by default, with an eye button
 * inside the field on the right that shows it (the icon turns to an eye with a
 * slash) and hides it again. Toggling keeps focus, the cursor and what's
 * typed; it goes back to hidden when its form is submitted, and on leaving the
 * screen (unmount). Pass the field's own className and autoComplete
 * ("current-password" / "new-password") so password managers keep working.
 * A password that isn't the person's login (a site's visitor password)
 * spreads NOT_A_LOGIN instead: "new-password" there made Chrome generate a
 * password and save it as their tinycms sign-in.
 */
export const NOT_A_LOGIN = {
  autoComplete: 'off',
  'data-1p-ignore': true,
  'data-lpignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
} as const;

export function PasswordInput({ className, style, ...rest }: Props) {
  const [shown, setShown] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  // Selection to put back after the type swap (some browsers reset it).
  const caret = useRef<[number | null, number | null] | null>(null);

  useEffect(() => {
    const form = ref.current?.form;
    if (!form) return undefined;
    const hide = () => setShown(false);
    form.addEventListener('submit', hide);
    return () => form.removeEventListener('submit', hide);
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    const saved = caret.current;
    caret.current = null;
    if (!el || !saved) return undefined;
    const restore = () => {
      if (document.activeElement !== el) return;
      if (el.selectionStart === saved[0] && el.selectionEnd === saved[1]) return;
      try {
        el.setSelectionRange(saved[0], saved[1]);
      } catch {
        // Not every input type allows a selection; nothing to restore.
      }
    };
    restore();
    // Chrome moves the cursor to the start a few frames after the type swap,
    // so put it back once more, unless the person types or clicks in the field.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const stop = () => {
      document.removeEventListener('selectionchange', restore);
      el.removeEventListener('keydown', stop);
      el.removeEventListener('mousedown', stop);
      el.removeEventListener('blur', stop);
      clearTimeout(timer);
    };
    document.addEventListener('selectionchange', restore);
    el.addEventListener('keydown', stop);
    el.addEventListener('mousedown', stop);
    el.addEventListener('blur', stop);
    timer = setTimeout(stop, 300);
    return stop;
  }, [shown]);

  const toggle = () => {
    const el = ref.current;
    if (el && document.activeElement === el) caret.current = [el.selectionStart, el.selectionEnd];
    setShown((s) => !s);
  };

  const label = shown ? 'Hide password' : 'Show password';
  return (
    <span className="ink-pw" style={style}>
      <input {...rest} ref={ref} type={shown ? 'text' : 'password'} className={className} />
      <button
        type="button"
        className="ink-pw-toggle"
        aria-label={label}
        aria-pressed={shown}
        title={label}
        // Keep focus (and the cursor) in the field when clicked with a mouse.
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggle}
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
          <circle cx="12" cy="12" r="3" />
          {shown ? <path d="M3 3l18 18" /> : null}
        </svg>
      </button>
    </span>
  );
}
